/**
 * @fileoverview /for-organizations — the page a prospective school, studio or
 * program is sent to.
 *
 * Why it exists. The organization story was spread across three
 * /features/organization-* pages, each covering a slice, and none of them an
 * overview — so a director had to visit three pages and assemble the product
 * themselves. Those pages stay as deep-dives; this is the one link to send.
 *
 * Why it is shaped as a term. What an organization is actually buying is not a
 * list of capabilities, it is the run of a term: publish the program, staff it,
 * build the schedule around a school calendar, fill the roster, collect
 * consent, teach, and get the record back out. Each step carries a photograph
 * of the real screen, so the claim that the steps connect is shown rather than
 * asserted. A capability grid cannot make that argument.
 *
 * Two rules this page must keep:
 *
 *   - **No org prices.** SUBSCRIPTION_PRICING_VISIBLE deliberately does not
 *     cover organization pricing (see lib/featureFlags) — the org checkout
 *     503s until its price ids are set. The billing section says how billing
 *     WORKS (per active student, the count shown before the invoice) and sends
 *     the reader to a person for the figure.
 *   - **The boundary is a feature.** What an organization cannot see is the
 *     reason instructors accept an invitation at all, so it gets its own
 *     section rather than a footnote.
 *
 * Screenshots come from `scripts/capture-marketing.mjs`; re-run it after a UI
 * change rather than editing images by hand.
 */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2,
  Briefcase,
  ShieldCheck,
  CalendarDays,
  Users,
  ClipboardList,
  BarChart3,
  Check,
  X,
  ArrowRight,
} from "lucide-react";

import PublicLayout from "@/components/PublicLayout";
import BrowserFrame from "@/components/BrowserFrame";
import Screenshot from "@/components/Screenshot";
import { supportEmail } from "@/lib/companyBrand";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const Eyebrow = ({ children }) => (
  <span className="mb-3 block font-mono text-[10px] uppercase tracking-widest text-[var(--accent-yellow-text)]">
    {children}
  </span>
);

const SectionHeading = ({ children }) => (
  <h2
    className="mb-3 text-2xl font-light tracking-tight md:text-4xl"
    style={{ fontFamily: "Outfit, sans-serif" }}
  >
    {children}
  </h2>
);

/* ------------------------------------------------------------------ */
/*  Content                                                            */
/* ------------------------------------------------------------------ */

/** What a director gets, before any of the detail. */
const PILLARS = [
  {
    icon: Building2,
    title: "One portal over every instructor",
    desc: "A separate login from your instructors' accounts, with its own admin team. What was taught, by whom, to how many students, and how many families have consent on file.",
  },
  {
    icon: CalendarDays,
    title: "Terms you build once",
    desc: "A class's meetings come from a weekly pattern or a list of dates, with the district's closure days imported rather than retyped. Every date is previewed before anything is written.",
  },
  {
    icon: ShieldCheck,
    title: "Consent as a gate, not a note",
    desc: "No instructor in your organization can record a student until that family has answered. Every request, grant, decline and withdrawal lands in a ledger you can export.",
  },
];

/**
 * The term, in the order it happens. `shot` is a marketing capture; `alt`
 * carries the meaning for anyone who cannot see it.
 */
const TERM_STEPS = [
  {
    icon: Briefcase,
    title: "Publish the program",
    body: "Post what you run — an after-school strings block, a summer intensive, a partner-school residency. Choose who can see it: every instructor on Hans, or only the addresses you name. Instructors apply with a note; you accept or decline.",
    shot: "/marketing/org-programs.webp",
    alt: "Program listings in the Hans organization portal, with visibility settings and incoming applications.",
  },
  {
    icon: Users,
    title: "Staff it",
    body: "Assign a class to any instructor who has accepted your organization, and doing so joins them to the program. Someone who has only been invited shows as provisional and cannot be given a class — a pending invitation is not agreement to teach. Substitutes cover single meetings without taking the class over.",
    shot: "/marketing/org-program-detail.webp",
    alt: "A program page in the Hans organization portal showing its instructors, classes and what has been taught.",
  },
  {
    icon: CalendarDays,
    title: "Build the term",
    body: "Describe a class that recurs — as many weekday-and-time pairs as it meets — or paste the dates for one that does not. Import the district's calendar file and its closure days are excluded up front rather than cancelled one at a time. Every date is previewed before the first write, and re-applying replaces the term rather than doubling it.",
    shot: "/marketing/org-class-schedule.webp",
    alt: "The class schedule builder in Hans, with a weekly pattern, term dates, and a box for importing the school calendar.",
  },
  {
    icon: ClipboardList,
    title: "Fill the roster",
    body: "Paste the roster or drop in a CSV. Every row is resolved and shown before anything is written — created, or already this instructor's student — and an import never rewrites a name, an address, or a consent answer a family already gave.",
    note: "Importing is not consent. Students created this way land with none, badged for review, and cannot be recorded until a family actually answers.",
    shot: "/marketing/org-class-roster.webp",
    alt: "A class roster in the Hans organization portal with each student's consent state shown beside their name.",
  },
  {
    icon: CalendarDays,
    title: "Teach the week",
    body: "Every class meeting across every program, a week at a time, coloured by program and filterable by instructor. The calendar opens on this week — who is teaching what, who is covering, and what has been called off.",
    note: "A private-lessons program is named rather than left out. Its bookings live in Kalver Scheduler, and silently omitting it would read as “that program has nothing on”.",
    shot: "/marketing/org-calendar.webp",
    alt: "The organization calendar in Hans, showing a week of class meetings coloured by program with substitutes marked.",
  },
  {
    icon: BarChart3,
    title: "Get the record back out",
    body: "How volume moved over any range you choose, which programs carried it, and a CSV of any dashboard tab you can pull yourself. A program's term downloads one row per meeting: date, class, instructor, substitute, whether it was taught or cancelled or still owes notes, attendance as a count, minutes, and the summary.",
    note: "Attendance is a count, never a list of names. Transcripts and instructors' private notes are never exported.",
    shot: "/marketing/org-reporting.webp",
    alt: "The Hans organization dashboard over the past 12 months: sessions by month, volume by program, and a CSV export.",
  },
];

/** The boundary, stated as two lists because that is how it is read. */
const VISIBLE = [
  "Lessons taught in your programs, and the summaries they produced",
  "Which students were reached, and how many were in the room",
  "Consent coverage per instructor, with imported students counted separately",
  "Who is teaching each meeting, and who is covering one",
  "Every administrative action, in an append-only ledger",
];

const NOT_VISIBLE = [
  "An instructor's private students, and anything taught outside your programs",
  "Instructors' own notes on a student",
  "Lesson transcripts — never shown, never exported",
  "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days.",
  "Attendance as a list of children's names",
];

const FAQS = [
  {
    q: "Do our instructors need their own Hans accounts?",
    a: "Yes, and that is deliberate. You invite them by email and they accept from their own account — joining means agreeing to let you see the teaching they do in your programs, so it has to be their decision. They can leave at any time, and your visibility ends when they do.",
  },
  {
    q: "We already use our own scheduling. Does Hans replace it?",
    a: "Class meetings live in Kalver Scheduler, which Hans connects to once during setup. Services, hours and payment settings stay there; Hans authors a class's term into it and mirrors the result for you and for each instructor.",
  },
  {
    q: "What happens to an instructor's private students?",
    a: "Nothing. They stay entirely separate from your organization, on the instructor's own plan, and never appear in your portal. That boundary is the reason instructors are willing to join.",
  },
  {
    q: "How are we billed?",
    a: "Per active student in a billing month — a student counts once a lesson has been recorded for them in one of your programs. The count, and the names behind it, are shown in your portal before the invoice is raised.",
  },
  {
    q: "Can we get our data out?",
    a: "Yes, whenever you want it, without asking us: a CSV of any tab on your dashboard, a term's spreadsheet from any program, and the consent ledger as its own export.",
  },
  {
    q: "What about students under 13?",
    a: "They follow COPPA specifically: a direct notice to the parent, a confirmation email carrying a cancel link, and lesson summaries delivered to the parent alone — never to the child.",
  },
];

const DEEP_DIVES = [
  {
    icon: Building2,
    title: "Organization Portal",
    desc: "Oversight, feature policy, and the audit trail.",
    to: "/features/organization-portal",
  },
  {
    icon: Briefcase,
    title: "Programs & Classes",
    desc: "Listings, staffing, terms, rosters, and attribution.",
    to: "/features/organization-programs",
  },
  {
    icon: ShieldCheck,
    title: "Consent & Compliance",
    desc: "The gate, the age bands, and the exportable ledger.",
    to: "/features/organization-compliance",
  },
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const OrganizationsPage = () => (
  <PublicLayout>
    <main className="safe-area-padding relative z-10">
      {/* ─── Hero ─── */}
      <section className="mx-auto max-w-5xl px-4 pt-12 pb-14 text-center md:px-8 md:pt-20 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <Eyebrow>For Organizations</Eyebrow>
          <h1
            className="mb-4 text-3xl font-light leading-tight tracking-tight md:text-5xl"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Run your programs. <span className="text-[var(--accent-yellow-text)]">Keep the record.</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base text-[var(--text-tertiary)] md:text-lg">
            Hans gives a school, studio or program its own portal: publish and staff programs, build
            a term around the school calendar, and get back a complete record of what was taught —
            while every instructor keeps their own students, and every family keeps their consent.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/org/signup"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium text-[var(--text-inverse)] transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              Create your organization <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
            <Link
              to="/org-guide"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-6 py-3 text-sm font-medium transition-colors duration-200 hover:bg-[var(--studio-overlay)] active:scale-95"
            >
              Read the organization guide
            </Link>
          </div>
        </motion.div>

        <motion.div {...fadeInUp} className="relative mt-12 md:mt-16" style={{ perspective: "1200px" }}>
          <BrowserFrame>
            <Screenshot
              src="/marketing/org-dashboard.webp"
              alt="The Hans organization dashboard: sessions taught, active programs, instructors teaching and students, with what needs attention."
              eager
            />
          </BrowserFrame>
        </motion.div>
      </section>

      {/* ─── Three pillars ─── */}
      <section className="mx-auto max-w-6xl px-4 pb-20 md:px-8 md:pb-28">
        <motion.div {...fadeInUp} className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {PILLARS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--studio-interactive)]">
                <Icon className="h-5 w-5 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
              </div>
              <h3 className="mb-2 text-base font-medium md:text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
                {title}
              </h3>
              <p className="text-sm leading-relaxed text-[var(--text-tertiary)]">{desc}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ─── How a term runs ─── */}
      <section className="mx-auto max-w-6xl px-4 pb-8 md:px-8">
        <motion.div {...fadeInUp} className="mb-12 text-center md:mb-16">
          <Eyebrow>How a term runs</Eyebrow>
          <SectionHeading>Six steps, start to finish</SectionHeading>
          <p className="mx-auto max-w-2xl text-sm text-[var(--text-tertiary)] md:text-base">
            Every screen below is the real portal. Nothing here is a mockup.
          </p>
        </motion.div>

        <div className="flex flex-col gap-16 md:gap-24">
          {TERM_STEPS.map(({ icon: Icon, title, body, note, shot, alt }, index) => (
            <motion.div
              key={title}
              {...fadeInUp}
              className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12"
            >
              {/* On desktop the image alternates sides; on mobile the words
                  always come first, because a screenshot with no caption above
                  it is a puzzle. */}
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-yellow-dim)] font-mono text-[12px] font-medium text-[var(--accent-yellow-text)]">
                    {index + 1}
                  </span>
                  <Icon className="h-5 w-5 text-[var(--text-tertiary)]" strokeWidth={1.5} />
                </div>
                <h3
                  className="mb-3 text-xl font-light tracking-tight md:text-2xl"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {title}
                </h3>
                <p className="max-w-[60ch] text-sm leading-relaxed text-[var(--text-tertiary)] md:text-base">
                  {body}
                </p>
                {note && (
                  <p className="mt-4 max-w-[60ch] border-l-2 border-[var(--accent-yellow-border)] pl-4 text-sm leading-relaxed text-[var(--text-secondary)]">
                    {note}
                  </p>
                )}
              </div>
              <div className={index % 2 === 1 ? "lg:order-1" : ""} style={{ perspective: "1200px" }}>
                <BrowserFrame>
                  <Screenshot src={shot} alt={alt} />
                </BrowserFrame>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Consent ─── */}
      <section className="mx-auto max-w-6xl px-4 pt-20 pb-20 md:px-8 md:pt-28 md:pb-28">
        <motion.div {...fadeInUp} className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <Eyebrow>Consent &amp; compliance</Eyebrow>
            <SectionHeading>The part you cannot afford to get wrong</SectionHeading>
            <p className="max-w-[60ch] text-sm leading-relaxed text-[var(--text-tertiary)] md:text-base">
              Most students in a school program are minors, so consent is enforced on the server
              every time a recording starts. An instructor who has not collected it does not get a
              warning they can dismiss — they get a blocked recording and a way to send the request.
              Paper consent can be recorded with an attestation, and it lands in the same ledger.
            </p>
            <p className="mt-4 max-w-[60ch] text-sm leading-relaxed text-[var(--text-tertiary)] md:text-base">
              Nothing an organization does grants consent — not a roster handed over, not a booking,
              not a contract with a school. The family answers, and can withdraw from any summary
              email they receive.
            </p>
            <Link
              to="/features/organization-compliance"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-yellow-text)] transition-opacity duration-200 hover:opacity-80"
            >
              How consent works in full <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
          <div style={{ perspective: "1200px" }}>
            <BrowserFrame>
              <Screenshot
                src="/marketing/consent-ledger.webp"
                alt="The append-only consent ledger in Hans, listing every request, grant and withdrawal with a CSV export."
              />
            </BrowserFrame>
          </div>
        </motion.div>
      </section>

      {/* ─── The boundary ─── */}
      <section className="mx-auto max-w-5xl px-4 pb-20 md:px-8 md:pb-28">
        <motion.div {...fadeInUp} className="mb-10 text-center md:mb-14">
          <Eyebrow>The boundary</Eyebrow>
          <SectionHeading>What you see, and what you never do</SectionHeading>
          <p className="mx-auto max-w-2xl text-sm text-[var(--text-tertiary)] md:text-base">
            An organization sees the teaching done in its own programs. The line is drawn in the
            product, not in a policy document — which is what makes instructors willing to join.
          </p>
        </motion.div>

        <motion.div {...fadeInUp} className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          <div className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-[var(--text-tertiary)]">
              You see
            </p>
            <ul className="flex flex-col gap-3">
              {VISIBLE.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
                  <Check
                    className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-success)]"
                    strokeWidth={1.8}
                  />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-[var(--text-tertiary)]">
              You never see
            </p>
            <ul className="flex flex-col gap-3">
              {NOT_VISIBLE.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.8} />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* ─── Billing ─── */}
      <section className="mx-auto max-w-6xl px-4 pb-20 md:px-8 md:pb-28">
        <motion.div {...fadeInUp} className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="lg:order-2">
            <Eyebrow>What it costs</Eyebrow>
            <SectionHeading>Per active student, shown before the bill</SectionHeading>
            <p className="max-w-[60ch] text-sm leading-relaxed text-[var(--text-tertiary)] md:text-base">
              A student counts as active in a month once a lesson has been recorded for them in one
              of your programs. You buy the platform rather than a number of seats, so there is
              nothing to hand out and no cap to run into — every instructor who is a member is
              covered, and the minutes they record in your programs count against the organization
              rather than against them.
            </p>
            <p className="mt-4 max-w-[60ch] text-sm leading-relaxed text-[var(--text-tertiary)] md:text-base">
              The count that produces the invoice is in your portal with the names behind it, and it
              says which state it is in: still counting, settling after the month closes, or final.
              A per-student price the payer cannot check is one they have to take on trust.
            </p>
            <a
              href={`mailto:${supportEmail()}?subject=Hans%20for%20our%20organization`}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-yellow-text)] transition-opacity duration-200 hover:opacity-80"
            >
              Talk to us about pricing <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </a>
          </div>
          <div className="lg:order-1" style={{ perspective: "1200px" }}>
            <BrowserFrame>
              <Screenshot
                src="/marketing/org-usage.webp"
                alt="The active students page in the Hans organization portal, showing the month's count, its state, and the students behind it."
              />
            </BrowserFrame>
          </div>
        </motion.div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="mx-auto max-w-4xl px-4 pb-20 md:px-8 md:pb-28">
        <motion.div {...fadeInUp} className="mb-10 text-center md:mb-14">
          <Eyebrow>Questions</Eyebrow>
          <SectionHeading>The ones we are asked first</SectionHeading>
        </motion.div>
        <motion.div {...fadeInUp} className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {FAQS.map(({ q, a }) => (
            <div
              key={q}
              className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6"
            >
              <h3 className="mb-2 text-sm font-medium md:text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
                {q}
              </h3>
              <p className="text-sm leading-relaxed text-[var(--text-tertiary)]">{a}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ─── Deep dives ─── */}
      <section className="mx-auto max-w-5xl px-4 pb-20 md:px-8 md:pb-28">
        <motion.div {...fadeInUp} className="mb-8 text-center">
          <Eyebrow>Go deeper</Eyebrow>
        </motion.div>
        <motion.div {...fadeInUp} className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {DEEP_DIVES.map(({ icon: Icon, title, desc, to }) => (
            <Link
              key={to}
              to={to}
              className="group flex h-full flex-col rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6 transition-colors duration-200 hover:border-[var(--studio-divider-strong)] hover:bg-[var(--studio-overlay)]"
            >
              <Icon
                className="mb-4 h-5 w-5 text-[var(--text-tertiary)] transition-colors duration-200 group-hover:text-[var(--accent-yellow-text)]"
                strokeWidth={1.5}
              />
              <h3 className="mb-2 text-base font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                {title}
              </h3>
              <p className="text-sm leading-relaxed text-[var(--text-tertiary)]">{desc}</p>
            </Link>
          ))}
        </motion.div>
      </section>

      {/* ─── Closing CTA ─── */}
      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-8 md:pb-32">
        <motion.div {...fadeInUp}>
          <SectionHeading>Set it up in an afternoon</SectionHeading>
          <p className="mx-auto mb-8 max-w-xl text-sm text-[var(--text-tertiary)] md:text-base">
            Create the organization, verify your email, connect scheduling, and invite your
            instructors. Your first program can be published the same day.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/org/signup"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium text-[var(--text-inverse)] transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              Create your organization <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-6 py-3 text-sm font-medium transition-colors duration-200 hover:bg-[var(--studio-overlay)] active:scale-95"
            >
              Talk to us first
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  </PublicLayout>
);

export default OrganizationsPage;
